import API_BASE from "./apiConfig";
import React, { useState, useRef, useEffect } from "react";
import {
  FaUser, FaEnvelope, FaPhone, FaKey, FaShieldAlt,
  FaUserPlus, FaUserEdit, FaSave, FaTimesCircle,
  FaSearch, FaCheckCircle, FaExclamationCircle,
  FaUsers, FaToggleOn, FaLink, FaUnlink, FaChild,
} from "react-icons/fa";
import { Colors, Shadows, Radii } from "./branding";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";
const BASE = API_BASE;
// ─── Design tokens (Golden Standard — do not alter) ───────────────────────────
const T = {
  glass:       "rgba(255,255,255,0.72)",
  glassBorder: "1px solid rgba(255,255,255,0.55)",
  glassShadow: "0 8px 32px rgba(0,40,100,0.10), 0 1.5px 6px rgba(0,40,100,0.06)",
  blur:        "blur(18px)",
  cardRadius:  "18px",
  inputRadius: "10px",
  yellow:      Colors.logoYellow,
  get blue() { return getBrandColors().blue; },
  get blueDark() { return getBrandColors().blueDark; },
  iconGray:    Colors.iconGray   || "#8899aa",
  inputBg:     "rgba(255,255,255,0.82)",
  font:        "'Segoe UI', system-ui, -apple-system, sans-serif",
};

// ─── Shared styles (Golden Standard) ──────────────────────────────────────────
const labelSt = {
  display: "block", marginBottom: "7px",
  fontSize: "12px", fontWeight: 700,
  letterSpacing: "0.06em", textTransform: "uppercase",
  color: T.blue, fontFamily: T.font,
};

const inputWrapSt = {
  display: "flex", alignItems: "center",
  border: "1.5px solid rgba(0,53,128,0.13)",
  borderRadius: T.inputRadius,
  padding: "10px 14px",
  background: T.inputBg,
  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
  boxShadow: "0 2px 8px rgba(0,40,100,0.06)",
  width: "100%", boxSizing: "border-box",
};

const inputSt = {
  border: "none", outline: "none", width: "100%",
  background: "transparent", fontSize: "14px",
  color: "#1a2840", fontFamily: T.font,
};

const lightText = {
  color: "#8899aa", fontWeight: 500,
  marginLeft: "4px", textTransform: "none", letterSpacing: 0,
};

const glassCard = {
  background: T.glass,
  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
  border: T.glassBorder, borderRadius: T.cardRadius,
  boxShadow: T.glassShadow,
  padding: "32px 36px", boxSizing: "border-box",
};

const yellowBtn = (T) => ({
  padding: "11px 22px", border: "none",
  borderRadius: "10px", fontSize: "14px",
  fontWeight: 700, cursor: "pointer", color: T.blue,
  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
  backgroundImage: `linear-gradient(135deg, ${T.yellow}, #fff3a0)`,
  boxShadow: "0 4px 14px rgba(255,200,0,0.35)", fontFamily: T.font,
});

const blueBtn = (T) => ({
  padding: "11px 22px",
  border: T.glassBdr,
  borderRadius: "10px", fontSize: "14px",
  fontWeight: 700, cursor: "pointer", color: T.textPrimary,
  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
  background: T.btnGlassBg,
  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
  boxShadow: T.glassSh,
  fontFamily: T.font,
});

const toastSt = (ok) => ({
  display: "flex", alignItems: "center", gap: "10px",
  padding: "13px 18px", borderRadius: "10px", marginBottom: "20px",
  fontSize: "14px", fontWeight: 600, fontFamily: T.font,
  background: ok ? "rgba(212,237,218,0.92)" : "rgba(248,215,218,0.92)",
  color:      ok ? "#155724"               : "#721c24",
  border:     ok ? "1px solid #b8dfc4"    : "1px solid #f3b8bc",
  backdropFilter: T.blur, WebkitBackdropFilter:T.blur,
  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
});

const tabBtnSt = (active) => ({
  padding: "10px 22px", borderRadius: "10px",
  fontSize: "13px", fontWeight: 700, cursor: "pointer",
  display: "flex", alignItems: "center", gap: "8px",
  letterSpacing: "0.02em", fontFamily: T.font,
  color:     active ? T.blue    : "#5a6a80",
  background: active ? `linear-gradient(135deg, ${T.yellow}, #fff3a0)` : T.glass,
  backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
  border:    active ? "1px solid rgba(255,200,0,0.4)" : T.glassBorder,
  boxShadow: active ? "0 4px 14px rgba(255,200,0,0.30)" : T.glassShadow,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

const blankForm = () => ({
  full_name: "", email: "", phone: "",
  password: "", user_role: "", active_status: 1, hourly_rate: "", pay_rate: "",
});

// ─── Assignable roles only (excludes Super Admin) ────────────────────────────
function useRoles() {
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/roles/assignable`)
      .then(r => r.json())
      .then(d => setRoles(d.roles || []))
      .catch(() => {});
  }, []);
  return roles;
}

// ─── Section divider (Golden Standard) ────────────────────────────────────────
function SectionLabel({ children }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  return (
    <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "10px", margin: "6px 0 0" }}>
      <div style={{ height: "2px", width: "18px", background: T.yellow, borderRadius: "2px" }} />
      <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase", color: T.textMuted, fontFamily: T.font }}>
        {children}
      </span>
      <div style={{ flex: 1, height: "1px", background: T.divider }} />
    </div>
  );
}

// ─── Reusable Field (Golden Standard) ─────────────────────────────────────────
function Field({ label, icon, name, type = "text", value, onChange, maxLength, error, placeholder }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const dynLabelSt = {
    display: "block", marginBottom: "7px",
    fontSize: "12px", fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase",
    color: T.textLabel, fontFamily: T.font,
  };
  const dynInputWrapSt = {
    display: "flex", alignItems: "center",
    border: `1.5px solid ${T.inputBdr}`,
    borderRadius: "10px",
    padding: "10px 14px",
    background: T.inputBg,
    backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
    boxShadow: "0 2px 8px rgba(0,40,100,0.06)",
    width: "100%", boxSizing: "border-box",
  };
  const dynInputSt = {
    border: "none", outline: "none", width: "100%",
    background: "transparent", fontSize: "14px",
    color: T.inputText, fontFamily: T.font,
  };
  return (
    <div>
      <label style={dynLabelSt}>{label}</label>
      <div style={dynInputWrapSt}>
        {icon && React.cloneElement(icon, { style: { marginRight: "10px", color: T.textMuted, flexShrink: 0 } })}
        <input type={type} name={name} value={value} onChange={onChange}
          style={dynInputSt} maxLength={maxLength} placeholder={placeholder} />
      </div>
      {error && (
        <p style={{ color: "#c0392b", fontSize: "11px", margin: "5px 0 0", display: "flex", alignItems: "center", gap: "4px", fontFamily: T.font }}>
          <FaExclamationCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, icon, name, value, onChange, options }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const dynLabelSt = {
    display: "block", marginBottom: "7px",
    fontSize: "12px", fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase",
    color: T.textLabel, fontFamily: T.font,
  };
  const dynInputWrapSt = {
    display: "flex", alignItems: "center",
    border: `1.5px solid ${T.inputBdr}`,
    borderRadius: "10px",
    padding: "10px 14px",
    background: T.inputBg,
    backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)",
    boxShadow: "0 2px 8px rgba(0,40,100,0.06)",
    width: "100%", boxSizing: "border-box",
  };
  const dynInputSt = {
    border: "none", outline: "none", width: "100%",
    background: "transparent", fontSize: "14px",
    color: T.inputText, fontFamily: T.font,
  };
  return (
    <div>
      <label style={dynLabelSt}>{label}</label>
      <div style={dynInputWrapSt}>
        {icon && React.cloneElement(icon, { style: { marginRight: "10px", color: T.textMuted, flexShrink: 0 } })}
        <select name={name} value={value} onChange={onChange}
          style={{ ...dynInputSt, cursor: "pointer", colorScheme: isDark ? "dark" : "light" }}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
  );
}

// ─── Role badge ────────────────────────────────────────────────────────────────
const roleColors = {
  Admin:  { bg: "rgba(155,89,182,0.10)", border: "rgba(155,89,182,0.25)", color: "#6c3483" },
  Coach:  { bg: "rgba(3,78,123,0.10)",   border: "rgba(3,78,123,0.25)",   color: getBrandColors().blueDark },
  Staff:  { bg: "rgba(39,174,96,0.10)",  border: "rgba(39,174,96,0.25)",  color: "#1a6e34" },
  Viewer: { bg: "rgba(127,140,141,0.12)", border: "rgba(127,140,141,0.28)", color: "#555" },
  Parent: { bg: "rgba(247,206,11,0.14)",  border: "rgba(247,206,11,0.40)",  color: "#7a6000" },
};

function RoleBadge({ role }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const s = roleColors[role] || roleColors.Viewer;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700, fontFamily: T.font,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
    }}>{role || "—"}</span>
  );
}

function StatusPill({ value }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const on = value === 1 || value === "1";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700, fontFamily: T.font,
      background: on ? "rgba(39,174,96,0.12)"  : "rgba(220,0,0,0.08)",
      border:     on ? "1px solid rgba(39,174,96,0.25)" : "1px solid rgba(220,0,0,0.18)",
      color:      on ? "#1a6e34" : "#b30000",
    }}>
      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: on ? "#27ae60" : "#e74c3c" }} />
      {on ? "Active" : "Inactive"}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CREATE PANEL
// ══════════════════════════════════════════════════════════════════════════════
function CreatePanel() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const roles               = useRoles();
  const [form, setForm]     = useState(blankForm());
  const [errors, setErrors] = useState({});
  const [msg, setMsg]       = useState(null);

  const validate = (name, value) => {
    let e = "";
    if (name === "email"    && value && !value.includes("@"))       e = "Must be a valid email address";
    if (name === "phone"    && value && value.replace(/\D/g,"").length !== 10) e = "Phone must be 10 digits";
    if (name === "password" && value && value.length < 6)           e = "Minimum 6 characters";
    setErrors((p) => ({ ...p, [name]: e }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") { const f = formatPhone(value); validate(name, f); setForm({ ...form, phone: f }); return; }
    validate(name, value);
    setForm({ ...form, [name]: value });
  };

  const clear = () => { setForm(blankForm()); setErrors({}); };  // msg is intentionally NOT cleared here

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password || !form.user_role) {
      setMsg({ ok: false, text: "Full name, email, password and role are required." }); return;
    }
    try {
      const res = await fetch(`${BASE}/users/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name:     form.full_name,
          email:         form.email,
          phone:         form.phone.replace(/\D/g, ""),
          password:      form.password,
          user_role:     form.user_role,
          active_status: Number(form.active_status),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMsg({ ok: true, text: "User created successfully!" });
        setForm(blankForm());   // reset fields only — leave the toast visible
        setErrors({});
      }
      else setMsg({ ok: false, text: data.message || "Error creating user" });
    } catch { setMsg({ ok: false, text: "Network error while creating user" }); }
  };

  return (
    <div>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}
      <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 28px" }}>

        <SectionLabel>Account Details</SectionLabel>
        <Field label="Full Name"     icon={<FaUser />}     name="full_name" value={form.full_name} onChange={handleChange} placeholder="First and last name" />
        <Field label="Email Address" icon={<FaEnvelope />} name="email"     value={form.email}     onChange={handleChange} placeholder="user@example.com" error={errors.email} />
        <Field label={<>Phone <span style={lightText}>(xxx-xxx-xxxx)</span></>}
               icon={<FaPhone />} name="phone" value={form.phone} onChange={handleChange} placeholder="000-000-0000" maxLength={12} error={errors.phone} />
        <Field label="Password" icon={<FaKey />} name="password" type="password"
               value={form.password} onChange={handleChange} placeholder="Min. 6 characters" error={errors.password} />

        <SectionLabel>Role &amp; Access</SectionLabel>
        <SelectField label="User Role"     icon={<FaShieldAlt />} name="user_role"     value={form.user_role}     onChange={handleChange}
          options={[["","Select role"], ...roles.map(r => [r.role_name, r.role_name])]} />
        <SelectField label="Active Status" icon={<FaToggleOn />}  name="active_status" value={form.active_status} onChange={handleChange}
          options={[[1,"Active"],[0,"Inactive"]]} />

        {form.user_role === "Coach" && (
          <>
            <Field label="Billing Rate / hr (USD)" icon={<FaKey />} name="hourly_rate"
                   value={form.hourly_rate} onChange={handleChange} placeholder="e.g. 70" type="number" />
            <Field label="Pay Rate / hr (USD)" icon={<FaKey />} name="pay_rate"
                   value={form.pay_rate} onChange={handleChange} placeholder="e.g. 35" type="number" />
          </>
        )}

        <div style={{ gridColumn: "1 / -1", display: "flex", gap: "14px", marginTop: "10px" }}>
          <button type="submit" style={{ ...yellowBtn(T), flex: 1, padding: "13px" }}><FaSave size={15} /> Create User</button>
          <button type="button" onClick={clear} style={{ ...blueBtn(T), flex: 1, padding: "13px" }}><FaTimesCircle size={15} /> Clear</button>
        </div>
      </form>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// UPDATE PANEL
// ══════════════════════════════════════════════════════════════════════════════
function UpdatePanel() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const dynLabelSt = { display:"block", marginBottom:"7px", fontSize:"12px", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:T.textLabel, fontFamily:T.font };
  const dynInputWrapSt = { display:"flex", alignItems:"center", border:`1.5px solid ${T.inputBdr}`, borderRadius:"10px", padding:"10px 14px", background:T.inputBg, backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)", boxShadow:"0 2px 8px rgba(0,40,100,0.06)", width:"100%", boxSizing:"border-box" };
  const dynInputSt = { border:"none", outline:"none", width:"100%", background:"transparent", fontSize:"14px", color:T.inputText, fontFamily:T.font };
  const roles              = useRoles();
  const [search,     setSearch]     = useState("");
  const [results,    setResults]    = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form,       setForm]       = useState(blankForm());
  const [errors,     setErrors]     = useState({});
  const [msg,        setMsg]        = useState(null);

  const validate = (name, value) => {
    let e = "";
    if (name === "email"    && value && !value.includes("@"))       e = "Must be a valid email address";
    if (name === "phone"    && value && value.replace(/\D/g,"").length !== 10) e = "Phone must be 10 digits";
    if (name === "password" && value && value.length < 6)           e = "Minimum 6 characters";
    setErrors((p) => ({ ...p, [name]: e }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") { const f = formatPhone(value); validate(name, f); setForm({ ...form, phone: f }); return; }
    validate(name, value);
    setForm({ ...form, [name]: value });
  };

  const doSearch = async (val) => {
    setSearch(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try {
      const res  = await fetch(`${BASE}/users/search?name=${encodeURIComponent(val)}`);
      const data = await res.json();
      setResults(data.users || []);
    } catch { setResults([]); }
  };

  const loadUser = async (id, label) => {
    setSelectedId(id); setSearch(label); setResults([]);
    try {
      const res  = await fetch(`${BASE}/users/${id}`);
      const data = await res.json();
      const u    = data.user;
      setForm({
        full_name:     u.full_name,
        email:         u.email,
        phone:         formatPhone(u.phone || ""),
        password:      "",
        user_role:     u.user_role,
        active_status: u.active_status,
        hourly_rate:   u.hourly_rate != null ? String(u.hourly_rate) : "",
        pay_rate:      u.pay_rate != null ? String(u.pay_rate) : "",
      });
    } catch { setMsg({ ok: false, text: "Failed to load user." }); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const payload = {
      full_name:     form.full_name,
      email:         form.email,
      phone:         form.phone.replace(/\D/g, ""),
      user_role:     form.user_role,
      active_status: Number(form.active_status),
      hourly_rate:   form.user_role === "Coach" && form.hourly_rate !== "" ? Number(form.hourly_rate) : null,
      pay_rate:      form.user_role === "Coach" && form.pay_rate !== "" ? Number(form.pay_rate) : null,
    };
    if (form.password) payload.password = form.password;

    try {
      const res  = await fetch(`${BASE}/users/${selectedId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) setMsg({ ok: true,  text: "User updated successfully!" });
      else              setMsg({ ok: false, text: data.message || "Error updating user" });
    } catch { setMsg({ ok: false, text: "Network error while updating user" }); }
  };

  return (
    <div>
      {msg && <div style={toastSt(msg.ok)}>{msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}</div>}

      {/* Search — identical pattern to StudentMaster UpdatePanel */}
      <div style={{ marginBottom: "24px", position: "relative" }}>
        <label style={dynLabelSt}>Search User</label>
        <div style={dynInputWrapSt}>
          <FaSearch style={{ marginRight: "10px", color: T.textMuted }} />
          <input type="text" value={search} onChange={(e) => doSearch(e.target.value)}
            placeholder="Type name or email…" style={dynInputSt} />
        </div>
        {results.length > 0 && (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, width: "100%",
            background: T.dropdownBg, backdropFilter: T.blur, WebkitBackdropFilter:T.blur,
            border: T.dropdownBdr, borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,40,100,0.14)", zIndex: 20,
            maxHeight: "200px", overflowY: "auto",
          }}>
            {results.map((u, i) => (
              <div key={u.user_id}
                onClick={() => loadUser(u.user_id, u.full_name)}
                style={{
                  padding: "11px 16px", cursor: "pointer", fontSize: "14px",
                  borderBottom: i < results.length - 1 ? "1px solid rgba(0,53,128,0.07)" : "none",
                  color: T.dropdownItem, fontWeight: 500,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  fontFamily: T.font,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,200,0,0.14)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <span><FaUser size={11} style={{ marginRight: "8px", color: T.textMuted }} />{u.full_name}</span>
                <span style={{ fontSize: "11px", color: T.textMuted }}>{u.email}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedId && (
        <form onSubmit={handleUpdate} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px 28px" }}>
          <SectionLabel>Account Details</SectionLabel>
          <Field label="Full Name"     icon={<FaUser />}     name="full_name" value={form.full_name} onChange={handleChange} />
          <Field label="Email Address" icon={<FaEnvelope />} name="email"     value={form.email}     onChange={handleChange} error={errors.email} />
          <Field label={<>Phone <span style={lightText}>(xxx-xxx-xxxx)</span></>}
                 icon={<FaPhone />} name="phone" value={form.phone} onChange={handleChange} maxLength={12} error={errors.phone} />
          <Field label={<>New Password <span style={lightText}>(leave blank to keep)</span></>}
                 icon={<FaKey />} name="password" type="password"
                 value={form.password} onChange={handleChange} placeholder="Leave blank to keep current" error={errors.password} />

          <SectionLabel>Role &amp; Access</SectionLabel>
          <SelectField label="User Role"     icon={<FaShieldAlt />} name="user_role"     value={form.user_role}     onChange={handleChange}
            options={[["","Select role"], ...roles.map(r => [r.role_name, r.role_name])]} />
          <SelectField label="Active Status" icon={<FaToggleOn />}  name="active_status" value={form.active_status} onChange={handleChange}
            options={[[1,"Active"],[0,"Inactive"]]} />

          {form.user_role === "Coach" && (
            <>
              <Field label="Billing Rate / hr (USD)" icon={<FaKey />} name="hourly_rate"
                     value={form.hourly_rate} onChange={handleChange} placeholder="e.g. 70" type="number" />
              <Field label="Pay Rate / hr (USD)" icon={<FaKey />} name="pay_rate"
                     value={form.pay_rate} onChange={handleChange} placeholder="e.g. 35" type="number" />
            </>
          )}

          <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
            <button type="submit" style={{ ...yellowBtn(T), width: "100%", padding: "13px" }}>
              <FaSave size={15} /> Update User
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ALL USERS PANEL
// ══════════════════════════════════════════════════════════════════════════════
function AllUsersPanel() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const dynLabelSt = { display:"block", marginBottom:"7px", fontSize:"12px", fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:T.textLabel, fontFamily:T.font };
  const dynInputWrapSt = { display:"flex", alignItems:"center", border:`1.5px solid ${T.inputBdr}`, borderRadius:"10px", padding:"10px 14px", background:T.inputBg, backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)", boxShadow:"0 2px 8px rgba(0,40,100,0.06)", width:"100%", boxSizing:"border-box" };
  const dynInputSt = { border:"none", outline:"none", width:"100%", background:"transparent", fontSize:"14px", color:T.inputText, fontFamily:T.font };
  const roles              = useRoles();
  const { fmtDate }         = useDateTimeFormat();
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filterRole,   setFilterRole]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchText,   setSearchText]   = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE}/users/`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    const q = searchText.toLowerCase();
    return (
      (!filterRole   || u.user_role === filterRole) &&
      (filterStatus === "" || String(u.active_status) === filterStatus) &&
      (!searchText   || u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    );
  });
  const usersSort = useSort(filtered, null);

  const thSt = {
    padding: "10px 14px", textAlign: "left", fontWeight: 800,
    color: T.textPrimary, fontSize: "11px", letterSpacing: "0.07em",
    textTransform: "uppercase", fontFamily: T.font,
    borderBottom: `1.5px solid ${T.divider}`,
    whiteSpace: "nowrap",
  };

  const tdSt = {
    padding: "10px 14px",
    borderBottom: T.rowBdr,
    color: T.textPrimary, fontSize: "13px", fontFamily: T.font,
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "2 1 200px" }}>
          <label style={dynLabelSt}>Search</label>
          <div style={dynInputWrapSt}>
            <FaSearch style={{ marginRight: "10px", color: T.textMuted }} />
            <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)}
              placeholder="Name or email…" style={dynInputSt} />
          </div>
        </div>
        <div style={{ flex: "1 1 140px" }}>
          <label style={dynLabelSt}>Role</label>
          <div style={dynInputWrapSt}>
            <FaShieldAlt style={{ marginRight: "10px", color: T.textMuted }} />
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ ...dynInputSt, cursor: "pointer", colorScheme: isDark ? "dark" : "light" }}>
              <option value="">All Roles</option>
              {roles.map(r => <option key={r.role_id} value={r.role_name}>{r.role_name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ flex: "1 1 130px" }}>
          <label style={dynLabelSt}>Status</label>
          <div style={dynInputWrapSt}>
            <FaToggleOn style={{ marginRight: "10px", color: T.textMuted }} />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...dynInputSt, cursor: "pointer", colorScheme: isDark ? "dark" : "light" }}>
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>
        <button onClick={load} style={{ ...yellowBtn(T), alignSelf: "flex-end", whiteSpace: "nowrap", padding: "11px 20px" }}>
          Refresh
        </button>
      </div>

      {/* Row count */}
      <div style={{ fontSize: "12px", color: T.textMuted, fontFamily: T.font, marginBottom: "12px", fontWeight: 600 }}>
        Showing {filtered.length} of {users.length} user{users.length !== 1 ? "s" : ""}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: T.textMuted, fontFamily: T.font }}>Loading…</div>
      ) : (
        <div style={{
          background: T.glass, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
          border: T.glassBdr, borderRadius: "14px", boxShadow: T.glassSh,
          overflow: "hidden",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "5%"  }} />  {/* # */}
              <col style={{ width: "20%" }} />  {/* Name */}
              <col style={{ width: "26%" }} />  {/* Email */}
              <col style={{ width: "15%" }} />  {/* Phone */}
              <col style={{ width: "12%" }} />  {/* Role */}
              <col style={{ width: "11%" }} />  {/* Status */}
              <col style={{ width: "11%" }} />  {/* Created */}
            </colgroup>
            <thead>
              <tr style={{ background: "rgba(0,53,128,0.07)" }}>
                {[["#","user_id"], ["Full Name","full_name"], ["Email","email"], ["Phone","phone"], ["Role","user_role"], ["Status","active_status"], ["Created","created_at"]].map(([h,key]) => (
                  <th key={h} onClick={() => usersSort.toggleSort(key)} style={{ ...thSt, cursor: "pointer", userSelect: "none" }}>{h}{usersSort.sortArrow(key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...tdSt, textAlign: "center", padding: "36px", color: T.textMuted }}>
                    No users found
                  </td>
                </tr>
              ) : usersSort.sorted.map((u, idx) => (
                <tr key={u.user_id}
                  style={{ background: idx % 2 === 0 ? T.rowEven : T.rowOdd }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,200,0,0.08)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? T.rowEven : T.rowOdd)}>
                  <td style={{ ...tdSt, color: T.textMuted, fontWeight: 600 }}>{u.user_id}</td>
                  <td style={{ ...tdSt, fontWeight: 700, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.full_name}
                  </td>
                  <td style={{ ...tdSt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</td>
                  <td style={tdSt}>{u.phone ? formatPhone(String(u.phone)) : "—"}</td>
                  <td style={tdSt}><RoleBadge role={u.user_role} /></td>
                  <td style={tdSt}><StatusPill value={u.active_status} /></td>
                  <td style={{ ...tdSt, color: T.textMuted, fontSize: "12px" }}>
                    {u.created_at ? fmtDate(u.created_at) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// PARENT LINK PANEL
// ══════════════════════════════════════════════════════════════════════════════
function ParentLinkPanel({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const dynInputWrapSt = { display:"flex", alignItems:"center", border:`1.5px solid ${T.inputBdr}`, borderRadius:"10px", padding:"10px 14px", background:T.inputBg, backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)", boxShadow:"0 2px 8px rgba(0,40,100,0.06)", width:"100%", boxSizing:"border-box" };
  const dynInputSt = { border:"none", outline:"none", width:"100%", background:"transparent", fontSize:"14px", color:T.inputText, fontFamily:T.font };
  const [parents,       setParents]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [expandedId,    setExpandedId]    = useState(null); // which parent row is open for linking
  const [studentSearch, setStudentSearch] = useState("");
  const [studentResults,setStudentResults]= useState([]);
  const [msg,           setMsg]           = useState(null);
  const [saving,        setSaving]        = useState(false);
  const parentsSort = useSort(parents, null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/users/parent-links`);
      const d = await r.json();
      setParents(d.parents || []);
    } catch { setParents([]); }
    finally   { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Debounced student search
  useEffect(() => {
    if (studentSearch.trim().length < 2) { setStudentResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BASE}/students/search?name=${encodeURIComponent(studentSearch)}`);
        const d = await r.json();
        setStudentResults(d.students || []);
      } catch { setStudentResults([]); }
    }, 280);
    return () => clearTimeout(t);
  }, [studentSearch]);

  const handleLink = async (parentUserId, studentId) => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch(`${BASE}/users/parent-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_user_id: parentUserId, student_id: studentId, updated_by: String(currentUserId || "system") }),
      });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message });
      if (d.success) {
        setExpandedId(null);
        setStudentSearch("");
        setStudentResults([]);
        load();
      }
    } catch { setMsg({ ok: false, text: "Network error" }); }
    finally   { setSaving(false); }
  };

  const handleUnlink = async (parentUserId) => {
    if (!window.confirm("Remove the student link for this parent?")) return;
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch(`${BASE}/users/parent-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parent_user_id: parentUserId, student_id: null, updated_by: String(currentUserId || "system") }),
      });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message });
      if (d.success) load();
    } catch { setMsg({ ok: false, text: "Network error" }); }
    finally   { setSaving(false); }
  };

  const thSt = {
    padding: "10px 14px", textAlign: "left", fontWeight: 800, color: T.textPrimary,
    fontSize: "11px", letterSpacing: "0.07em", textTransform: "uppercase",
    fontFamily: T.font, borderBottom: `1.5px solid ${T.divider}`,
  };
  const tdSt = {
    padding: "10px 14px", borderBottom: T.rowBdr,
    color: T.textPrimary, fontSize: "13px", fontFamily: T.font, verticalAlign: "top",
  };

  return (
    <div>
      {/* Info banner */}
      <div style={{
        display: "flex", alignItems: "flex-start", gap: 14,
        padding: "14px 18px", borderRadius: 12, marginBottom: 20,
        background: "rgba(247,206,11,0.10)", border: "1px solid rgba(247,206,11,0.35)",
      }}>
        <FaChild size={16} style={{ color: "#7a6000", flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, color: "#5a4800", fontFamily: T.font, lineHeight: 1.6 }}>
          <strong>How it works:</strong> A Parent account logs in using the email address that is saved
          on their child's Student Master record (Parent Email field). Use this panel to manage that
          link — search for the student and click <strong>Link</strong> to connect them, or{" "}
          <strong>Unlink</strong> to remove the connection.
        </div>
      </div>

      {msg && (
        <div style={{ ...toastSt(msg.ok), marginBottom: 16 }}>
          {msg.ok ? <FaCheckCircle /> : <FaExclamationCircle />} {msg.text}
        </div>
      )}

      {/* Refresh */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button onClick={load} style={{ ...yellowBtn(T), padding: "9px 18px", fontSize: 12 }}>
          Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: T.textMuted, fontFamily: T.font }}>Loading…</div>
      ) : parents.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 24px",
          background: T.glass, backdropFilter: T.blur, WebkitBackdropFilter:T.blur, borderRadius: 14,
          border: T.glassBdr, color: T.textMuted, fontFamily: T.font, fontSize: 14,
        }}>
          No Parent role users found. Create a user with role "Parent" first.
        </div>
      ) : (
        <div style={{
          background: T.glass, backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
          border: T.glassBdr, borderRadius: 14, boxShadow: T.glassSh, overflow: "hidden",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "24%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "14%" }} />
            </colgroup>
            <thead>
              <tr style={{ background: "rgba(0,53,128,0.07)" }}>
                {[["Parent Name","full_name"], ["Login Email","email"], ["Status","active_status"], ["Linked Student",null], ["Actions",null]].map(([h,key]) => (
                  <th key={h} onClick={key ? () => parentsSort.toggleSort(key) : undefined}
                    style={{ ...thSt, ...(key ? { cursor: "pointer", userSelect: "none" } : {}) }}>{h}{key ? parentsSort.sortArrow(key) : ""}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parentsSort.sorted.map((p, idx) => {
                const isOpen = expandedId === p.user_id;
                const s = p.linked_student;
                return (
                  <React.Fragment key={p.user_id}>
                    {/* Main row */}
                    <tr style={{ background: idx % 2 === 0 ? T.rowEven : T.rowOdd }}>
                      <td style={{ ...tdSt, fontWeight: 700, color: T.textPrimary }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                            background: `linear-gradient(135deg,${T.yellow},#fff3a0)`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 900, color: T.blue,
                          }}>
                            {p.full_name?.charAt(0).toUpperCase()}
                          </div>
                          {p.full_name}
                        </div>
                      </td>
                      <td style={{ ...tdSt, color: "#5a6a80", fontSize: 12 }}>{p.email}</td>
                      <td style={tdSt}><StatusPill value={p.active_status} /></td>
                      <td style={tdSt}>
                        {s ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {/* Student avatar */}
                            <div style={{
                              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                              background: "rgba(3,78,123,0.10)", border: "1.5px solid rgba(3,78,123,0.18)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 10, fontWeight: 800, color: T.textPrimary, overflow: "hidden",
                            }}>
                              {s.photo_url
                                ? <img src={`${BASE}/${s.photo_url}`} alt=""
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                : `${s.first_name?.[0] || ""}${s.last_name?.[0] || ""}`}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, color: T.textPrimary, fontSize: 13 }}>
                                {s.first_name} {s.last_name}
                              </div>
                              <div style={{ fontSize: 11, color: T.textMuted }}>ID #{s.student_id}</div>
                            </div>
                            <div style={{
                              marginLeft: 4, padding: "2px 8px", borderRadius: 20, fontSize: 10,
                              fontWeight: 700, fontFamily: T.font,
                              background: s.active_status ? "rgba(39,174,96,0.10)" : "rgba(220,0,0,0.07)",
                              border: s.active_status ? "1px solid rgba(39,174,96,0.25)" : "1px solid rgba(220,0,0,0.18)",
                              color: s.active_status ? "#1a6e34" : "#b30000",
                            }}>
                              {s.active_status ? "Active" : "Inactive"}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#c0392b", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                            <FaUnlink size={11} /> Not linked
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdSt }}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            onClick={() => { setExpandedId(isOpen ? null : p.user_id); setStudentSearch(""); setStudentResults([]); }}
                            style={{
                              padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                              fontSize: 11, fontWeight: 700, fontFamily: T.font,
                              background: isOpen ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : "rgba(3,78,123,0.10)",
                              color: isOpen ? T.blue : T.textPrimary,
                              boxShadow: isOpen ? "0 2px 8px rgba(255,200,0,0.30)" : "none",
                              display: "flex", alignItems: "center", gap: 5,
                            }}>
                            <FaLink size={10} /> {s ? "Change" : "Link"}
                          </button>
                          {s && (
                            <button
                              onClick={() => handleUnlink(p.user_id)}
                              disabled={saving}
                              style={{
                                padding: "6px 12px", borderRadius: 8, cursor: "pointer",
                                fontSize: 11, fontWeight: 700, fontFamily: T.font,
                                background: "rgba(220,0,0,0.08)", border: "1px solid rgba(220,0,0,0.20)",
                                color: "#c0392b", display: "flex", alignItems: "center", gap: 5,
                              }}>
                              <FaUnlink size={10} /> Unlink
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded link row */}
                    {isOpen && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, borderBottom: "2px solid rgba(247,206,11,0.4)" }}>
                          <div style={{
                            padding: "16px 20px",
                            background: "rgba(247,206,11,0.06)",
                            borderTop: "1px solid rgba(247,206,11,0.20)",
                          }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: T.textPrimary, fontFamily: T.font,
                              textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                              Link Student to <span style={{ color: "#7a6000" }}>{p.full_name}</span>
                            </div>
                            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                              {/* Search input */}
                              <div style={{ flex: "1 1 260px", position: "relative" }}>
                                <div style={dynInputWrapSt}>
                                  <FaSearch style={{ marginRight: 10, color: T.textMuted, flexShrink: 0 }} />
                                  <input
                                    autoFocus
                                    value={studentSearch}
                                    onChange={e => setStudentSearch(e.target.value)}
                                    placeholder="Search student by name…"
                                    style={dynInputSt}
                                  />
                                </div>
                                {/* Dropdown results */}
                                {studentResults.length > 0 && (
                                  <div style={{
                                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
                                    background: T.dropdownBg, backdropFilter: T.blur, WebkitBackdropFilter:T.blur,
                                    border: T.dropdownBdr, borderRadius: 12,
                                    boxShadow: "0 8px 24px rgba(3,78,123,0.16)",
                                    maxHeight: 240, overflowY: "auto",
                                  }}>
                                    {studentResults.map((st, si) => (
                                      <div key={st.student_id}
                                        onClick={() => handleLink(p.user_id, st.student_id)}
                                        style={{
                                          padding: "10px 16px", cursor: "pointer",
                                          borderBottom: si < studentResults.length - 1 ? "1px solid rgba(0,53,128,0.07)" : "none",
                                          display: "flex", alignItems: "center", gap: 10,
                                          fontSize: 13, fontFamily: T.font,
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,200,0,0.14)"}
                                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                        <div style={{
                                          width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                                          background: "rgba(3,78,123,0.10)", border: "1.5px solid rgba(3,78,123,0.15)",
                                          display: "flex", alignItems: "center", justifyContent: "center",
                                          fontSize: 11, fontWeight: 800, color: T.textPrimary,
                                        }}>
                                          {st.first_name?.[0]}{st.last_name?.[0]}
                                        </div>
                                        <div>
                                          <div style={{ fontWeight: 700, color: T.textPrimary }}>{st.first_name} {st.last_name}</div>
                                          <div style={{ fontSize: 11, color: T.textMuted }}>Student ID #{st.student_id}</div>
                                        </div>
                                        <div style={{ marginLeft: "auto" }}>
                                          <span style={{
                                            padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                                            background: "rgba(3,78,123,0.08)", border: "1px solid rgba(3,78,123,0.15)",
                                            color: T.textPrimary, display: "flex", alignItems: "center", gap: 5,
                                          }}>
                                            <FaLink size={9} /> Click to Link
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {studentSearch.length >= 2 && studentResults.length === 0 && (
                                  <div style={{
                                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
                                    background: T.dropdownBg, backdropFilter: T.blur, WebkitBackdropFilter:T.blur,
                                    border: T.dropdownBdr, borderRadius: 12,
                                    padding: "14px 16px", fontSize: 13, color: T.textMuted, fontFamily: T.font,
                                  }}>
                                    No students found for "{studentSearch}"
                                  </div>
                                )}
                              </div>
                              {/* Cancel button */}
                              <button
                                onClick={() => { setExpandedId(null); setStudentSearch(""); setStudentResults([]); }}
                                style={{ ...blueBtn(T), padding: "11px 18px", fontSize: 12, alignSelf: "flex-start" }}>
                                Cancel
                              </button>
                            </div>
                            <p style={{ margin: "10px 0 0", fontSize: 11, color: T.textMuted, fontFamily: T.font }}>
                              Type at least 2 characters to search. Click a student to immediately link them to this parent.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN — UserMaster
// ══════════════════════════════════════════════════════════════════════════════
const UserMaster = ({ currentUserId }) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [activeTab, setActiveTab] = useState("create");

  const dynGlassCard = {
    background: T.glass,
    backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
    border: T.glassBdr, borderRadius: "18px",
    boxShadow: T.glassSh,
    padding: "32px 36px", boxSizing: "border-box",
  };

  const dynTabBtnSt = (active) => ({
    padding: "10px 22px", borderRadius: "10px",
    fontSize: "13px", fontWeight: 700, cursor: "pointer",
    display: "flex", alignItems: "center", gap: "8px",
    letterSpacing: "0.02em", fontFamily: T.font,
    color:      active ? T.blue    : T.textLabel,
    background: active ? `linear-gradient(135deg, ${T.yellow}, #fff3a0)` : T.glass,
    backdropFilter: T.blur, WebkitBackdropFilter: T.blur,
    border:    active ? "1px solid rgba(255,200,0,0.4)" : T.glassBdr,
    boxShadow: active ? "0 4px 14px rgba(255,200,0,0.30)" : T.glassSh,
  });

  const tabs = [
    { key: "create",      label: "Create User",   icon: <FaUserPlus size={14} /> },
    { key: "update",      label: "Update User",   icon: <FaUserEdit size={14} /> },
    { key: "list",        label: "All Users",     icon: <FaUsers    size={14} /> },
    { key: "parentlink",  label: "Parent Links",  icon: <FaLink     size={14} /> },
  ];

  return (
    <div style={{ minHeight: "100%", padding: "20px 28px 48px", boxSizing: "border-box", fontFamily: T.font }}>

      {/* Page Header — bare, left-aligned (Golden Standard) */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "20px" }}>
        <PageHeaderIcon icon={<FaUsers size={20} color={getBrandColors().blueDark} />} />
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: 800, color: T.textPrimary, margin: 0, letterSpacing: "-0.01em", fontFamily: T.font }}>
            User Master
          </h2>
          <p style={{ margin: 0, fontSize: "12px", color: T.textMuted, marginTop: "1px", fontFamily: T.font }}>
            Create, update and manage system user accounts
          </p>
        </div>
      </div>

      {/* Tabs (Golden Standard) */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={dynTabBtnSt(activeTab === t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Panel card — centred, full-width on list tab (Golden Standard) */}
      <div style={{
        ...dynGlassCard,
        maxWidth: (activeTab === "list" || activeTab === "parentlink") ? "100%" : "860px",
        margin: "0 auto",
        transition: "max-width 0.3s",
      }}>
        {activeTab === "create"     && <CreatePanel currentUserId={currentUserId} />}
        {activeTab === "update"     && <UpdatePanel currentUserId={currentUserId} />}
        {activeTab === "list"       && <AllUsersPanel />}
        {activeTab === "parentlink" && <ParentLinkPanel currentUserId={currentUserId} />}
      </div>

    </div>
  );
};

export default UserMaster;
